import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

function formatDate(dateStr) {
  if (!dateStr) return "—";
  // YYYY-MM-DD → DD-MM-YYYY
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return dateStr;
}

function getBadgeClass(dateStr) {
  if (!dateStr) return { label: "N/A", cls: "bg-gray-100 text-gray-500" };
  let d;
  const m1 = dateStr.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/);
  const m2 = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m1) d = new Date(`${m1[2]} ${m1[1]}, ${m1[3]}`);
  else if (m2) d = new Date(dateStr);
  else return { label: "N/A", cls: "bg-gray-100 text-gray-500" };
  if (isNaN(d)) return { label: "N/A", cls: "bg-gray-100 text-gray-500" };
  const diff = Math.ceil((d - new Date()) / 86400000);
  if (diff < 0) return { label: "Expired", cls: "bg-red-50 text-red-600 ring-1 ring-red-200" };
  if (diff <= 30) return { label: "Expiring soon", cls: "bg-amber-50 text-amber-600 ring-1 ring-amber-200" };
  return { label: "Valid", cls: "bg-green-50 text-green-600 ring-1 ring-green-200" };
}

function Badge({ dateStr }) {
  const { label, cls } = getBadgeClass(dateStr);
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide ${cls}`}>
      {label}
    </span>
  );
}

function DocPill({ label, dateStr }) {
  const { cls } = getBadgeClass(dateStr);
  return (
    <div className={`flex flex-col gap-0.5 px-3 py-2 rounded-lg ${cls} min-w-0`}>
      <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</span>
      <span className="text-xs font-semibold truncate">{formatDate(dateStr)}</span>
    </div>
  );
}

// ── Detail modal ──────────────────────────────────────────────────────────────
function VehicleDetailModal({ vehicle, onClose }) {
  if (!vehicle) return null;

  const isActive = (vehicle.registration_status || "").toUpperCase() === "ACTIVE";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.35)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl border-2 border-blue-100 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex items-center gap-4 p-6 border-b-2 border-blue-50">
          <div className="w-14 h-14 rounded-xl bg-blue-100 border-2 border-blue-200 flex items-center justify-center text-3xl shrink-0">
            🚛
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xl font-bold text-gray-900 truncate">
              {[vehicle.maker, vehicle.model].filter(Boolean).join(" ") || "—"}
            </p>
            <p className="text-sm text-gray-500 mt-0.5">
              {[vehicle.body_type, vehicle.vehicle_category, vehicle.fuel_type, vehicle.color].filter(Boolean).join(" · ")}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${
              isActive ? "bg-green-50 text-green-700 ring-2 ring-green-200" : "bg-red-50 text-red-700 ring-2 ring-red-200"
            }`}>
              {isActive ? "✓ Active" : "✕ Inactive"}
            </span>
            <span className="text-xs text-gray-500 font-semibold">{vehicle.registration_number}</span>
          </div>
          <button
            onClick={onClose}
            className="ml-2 w-8 h-8 rounded-lg border-2 border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:border-gray-300 transition shrink-0"
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-6">

          {/* Vehicle identity */}
          <Section title="Vehicle identity" icon="🚗">
            <Grid>
              <Cell label="Registration no." value={vehicle.registration_number} />
              <Cell label="Chassis no." value={vehicle.chassis_number} />
              <Cell label="Engine no." value={vehicle.engine_number} />
              <Cell label="Make" value={vehicle.maker} />
              <Cell label="Model" value={vehicle.model} />
              <Cell label="Body type" value={vehicle.body_type} />
              <Cell label="Fuel type" value={vehicle.fuel_type} />
              <Cell label="Colour" value={vehicle.color} />
              <Cell label="Year of manufacture" value={vehicle.manufacture_month_year} />
              <Cell label="GVW" value={vehicle.gross_vehicle_weight} />
              <Cell label="Seating capacity" value={vehicle.seating_capacity} />
              <Cell label="Category" value={vehicle.vehicle_category} />
              <Cell label="Insurance company" value={vehicle.insurance_company} />
            </Grid>
          </Section>

          {/* Owner */}
          <Section title="Owner details" icon="👤">
            <Grid>
              <Cell label="Owner name" value={vehicle.owner_name} />
              <Cell label="Phone number" value={vehicle.phone_number} />
              <Cell label="RTO code" value={vehicle.rto_code} />
              <Cell label="Registration date" value={vehicle.registration_date} />
              <div className="col-span-2 md:col-span-4">
                <Cell label="Address" value={vehicle.owner_address} />
              </div>
            </Grid>
          </Section>

          {/* Documents */}
          <Section title="Documents & validity" icon="🛡️">
            <Grid>
              <CellWithBadge label="Insurance" date={vehicle.insurance_valid_upto} sub={vehicle.insurance_company} />
              <CellWithBadge label="Fitness" date={vehicle.fitness_valid_upto} />
              <CellWithBadge label="PUC" date={vehicle.puc_valid_upto} />
              <CellWithBadge label="Tax" date={vehicle.tax_valid_upto} />
              <CellWithBadge label="National permit" date={vehicle.national_permit_valid_upto} />
              <CellWithBadge label="5-year permit" date={vehicle.five_year_permit_valid_upto} />
            </Grid>
          </Section>

          {/* DL — only if present */}
          {vehicle.searched_dl_number && (
            <Section title="Driver licence" icon="🪪">
              <Grid>
                <Cell label="Licence no." value={vehicle.searched_dl_number} />
                <Cell label="Date of birth" value={formatDate(vehicle.searched_date_of_birth)} />
                {vehicle.dl_transport_valid_upto && (
                  <CellWithBadge label="Transport validity" date={vehicle.dl_transport_valid_upto} sub="For commercial vehicles" />
                )}
                {vehicle.dl_non_transport_valid_upto && (
                  <CellWithBadge label="Non-transport validity" date={vehicle.dl_non_transport_valid_upto} sub="For personal vehicles" />
                )}
              </Grid>
            </Section>
          )}

          {/* Meta */}
          <div className="text-xs text-gray-400 text-right">
            Registered on {formatDate(vehicle.created_at)}
          </div>

        </div>
      </div>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-blue-50">
        <span>{icon}</span>
        <span className="text-xs font-bold text-blue-700 uppercase tracking-wider">{title}</span>
      </div>
      {children}
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">{children}</div>;
}

function Cell({ label, value }) {
  return (
    <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-3">
      <p className="text-[10px] font-bold text-blue-500 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-sm font-medium text-gray-900 break-words">{value || "—"}</p>
    </div>
  );
}

function CellWithBadge({ label, date, sub }) {
  return (
    <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-3">
      <p className="text-[10px] font-bold text-blue-500 uppercase tracking-wider mb-1.5">{label}</p>
      <div className="flex items-center justify-between gap-1 mb-1">
        <span className="text-sm font-medium text-gray-900">{formatDate(date)}</span>
        <Badge dateStr={date} />
      </div>
      {sub && <p className="text-[11px] text-gray-500 truncate">{sub}</p>}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function RegisteredVehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const baseURL = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.get(`${baseURL}/api/ulip/registered-vehicles`);
      setVehicles(res.data.vehicles || []);
    } catch (err) {
      setError("Failed to load registered vehicles.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  // Filter
  const q = search.trim().toLowerCase();
  const filtered = vehicles.filter((v) =>
    !q ||
    (v.registration_number || "").toLowerCase().includes(q) ||
    (v.owner_name || "").toLowerCase().includes(q) ||
    (v.maker || "").toLowerCase().includes(q) ||
    (v.model || "").toLowerCase().includes(q) ||
    (v.searched_vehicle_number || "").toLowerCase().includes(q) ||
    (v.phone_number || "").toLowerCase().includes(q)
  );

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  // Stats
  const total = vehicles.length;
  const active = vehicles.filter(v => (v.registration_status || "").toUpperCase() === "ACTIVE").length;
  const expiredInsurance = vehicles.filter(v => getBadgeClass(v.insurance_valid_upto).label === "Expired").length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 px-4 py-10 text-gray-900">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-bold text-blue-900">Registered Vehicles</h1>
          <button
            onClick={fetchVehicles}
            className="h-10 px-5 rounded-lg border-2 border-blue-200 text-blue-700 hover:bg-blue-50 text-sm font-semibold transition flex items-center gap-2"
          >
            ↻ Refresh
          </button>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white border-2 border-blue-100 rounded-2xl p-5 shadow-sm">
            <p className="text-xs font-bold text-blue-500 uppercase tracking-wider mb-1">Total vehicles</p>
            <p className="text-3xl font-bold text-blue-900">{total}</p>
          </div>
          <div className="bg-white border-2 border-green-100 rounded-2xl p-5 shadow-sm">
            <p className="text-xs font-bold text-green-600 uppercase tracking-wider mb-1">Active</p>
            <p className="text-3xl font-bold text-green-700">{active}</p>
          </div>
          <div className="bg-white border-2 border-red-100 rounded-2xl p-5 shadow-sm">
            <p className="text-xs font-bold text-red-500 uppercase tracking-wider mb-1">Expired insurance</p>
            <p className="text-3xl font-bold text-red-600">{expiredInsurance}</p>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white border-2 border-blue-100 rounded-2xl px-5 py-4 shadow-sm mb-4 flex items-center gap-3">
          <span className="text-gray-400 text-lg">🔍</span>
          <input
            value={search}
            onChange={handleSearch}
            placeholder="Search by registration no., owner, phone, make or model…"
            className="flex-1 text-sm text-gray-900 placeholder-gray-400 bg-transparent focus:outline-none"
          />
          {search && (
            <button onClick={() => { setSearch(""); setPage(1); }} className="text-gray-400 hover:text-gray-600 text-sm">✕</button>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 bg-red-50 border-2 border-red-200 rounded-xl px-5 py-4 mb-4">
            <span className="text-red-500">⚠</span>
            <p className="text-sm text-red-700 font-medium">{error}</p>
          </div>
        )}

        {/* Table */}
        <div className="bg-white border-2 border-blue-100 rounded-2xl shadow-lg overflow-hidden">

          {loading ? (
            <div className="flex items-center justify-center py-20 text-blue-400">
              <svg className="animate-spin w-6 h-6 mr-3" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              <span className="text-sm font-medium">Loading vehicles…</span>
            </div>
          ) : paginated.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <span className="text-5xl mb-3">🚛</span>
              <p className="text-sm font-medium">{search ? "No vehicles match your search." : "No registered vehicles yet."}</p>
            </div>
          ) : (
            <>
              {/* Table header */}
              <div className="grid grid-cols-12 gap-3 px-5 py-3 bg-blue-50 border-b-2 border-blue-100 text-[11px] font-bold text-blue-600 uppercase tracking-wider">
                <div className="col-span-2">Reg. no.</div>
                <div className="col-span-3">Vehicle</div>
                <div className="col-span-2">Owner</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-3">Documents Status</div>
                <div className="col-span-1">Registered</div>
              </div>

              {/* Rows */}
              {paginated.map((v, i) => {
                const isActive = (v.registration_status || "").toUpperCase() === "ACTIVE";
                return (
                  <div
                    key={v.id}
                    onClick={() => setSelected(v)}
                    className={`grid grid-cols-12 gap-3 px-5 py-4 items-center cursor-pointer transition hover:bg-blue-50/60 ${
                      i !== paginated.length - 1 ? "border-b border-blue-50" : ""
                    }`}
                  >
                    {/* Reg no */}
                    <div className="col-span-2">
                      <span className="text-sm font-bold text-blue-700 font-mono">
                        {v.registration_number || v.searched_vehicle_number || "—"}
                      </span>
                    </div>

                    {/* Vehicle */}
                    <div className="col-span-3 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {[v.maker, v.model].filter(Boolean).join(" ") || "—"}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {[v.vehicle_category, v.fuel_type].filter(Boolean).join(" · ")}
                      </p>
                    </div>

                    {/* Owner */}
                    <div className="col-span-2 min-w-0">
                      <p className="text-sm text-gray-700 truncate">{v.owner_name || "—"}</p>
                      <p className="text-xs text-gray-400">{v.rto_code || ""}</p>
                    </div>

                    {/* Status */}
                    <div className="col-span-1">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold ${
                        isActive
                          ? "bg-green-50 text-green-700 ring-1 ring-green-200"
                          : "bg-red-50 text-red-600 ring-1 ring-red-200"
                      }`}>
                        {isActive ? "Active" : "Inactive"}
                      </span>
                    </div>

                    {/* Doc pills */}
                    <div className="col-span-3 flex flex-wrap gap-1.5">
                      <DocPill label="Ins." dateStr={v.insurance_valid_upto} />
                      <DocPill label="Fit." dateStr={v.fitness_valid_upto} />
                      <DocPill label="PUC" dateStr={v.puc_valid_upto} />
                    </div>

                    {/* Registered date */}
                    <div className="col-span-1">
                      <p className="text-xs text-gray-400">{formatDate(v.created_at)}</p>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Pagination */}
        {!loading && filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-4 px-1">
            <p className="text-sm text-gray-500">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="h-9 px-4 rounded-lg border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:border-blue-300 hover:text-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                ← Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === "..." ? (
                    <span key={`ellipsis-${i}`} className="h-9 px-2 flex items-center text-gray-400 text-sm">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`h-9 w-9 rounded-lg border-2 text-sm font-semibold transition ${
                        page === p
                          ? "bg-blue-600 border-blue-600 text-white"
                          : "border-gray-200 text-gray-600 hover:border-blue-300 hover:text-blue-600"
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="h-9 px-4 rounded-lg border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:border-blue-300 hover:text-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                Next →
              </button>
            </div>
          </div>
        )}

      </div>

      {/* Detail modal */}
      {selected && <VehicleDetailModal vehicle={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}